import { z } from 'zod';
import * as dt from './data.types.js';
/** Cognex EV light mode 0–14 */
export const COGNEX_LIGHT_MODE_MIN = 0;
export const COGNEX_LIGHT_MODE_MAX = 14;
/**
 * Modes 12–14 (Custom LED / advanced presets) require extra hardware and commissioning.
 * Station acquire and other operator UIs expose 0–11 only.
 */
export const COGNEX_LIGHT_MODE_OPERATOR_MAX = 11;
export const cognexLightModeSchema = z.number().int().min(COGNEX_LIGHT_MODE_MIN).max(COGNEX_LIGHT_MODE_MAX);
/** Cognex EV light power 0–127; auto-adjust binary search may emit floats at API boundaries. */
export const cognexLightPowerSchema = z.preprocess((val) => (typeof val === 'number' ? Math.round(val) : val), z.number().int().min(0).max(127));
export const cognexLightPowerOptionalSchema = cognexLightPowerSchema.optional();
/** Every Cognex EV lighting preset supported by the camera API. */
export const ALL_COGNEX_LIGHT_MODES = Array.from({ length: COGNEX_LIGHT_MODE_MAX - COGNEX_LIGHT_MODE_MIN + 1 }, (_, i) => COGNEX_LIGHT_MODE_MIN + i);
/** Operator-selectable modes (excludes 12–14). */
export const OPERATOR_COGNEX_LIGHT_MODES = Array.from({ length: COGNEX_LIGHT_MODE_OPERATOR_MAX - COGNEX_LIGHT_MODE_MIN + 1 }, (_, i) => COGNEX_LIGHT_MODE_MIN + i);
export const LightModeEntrySchema = z.object({
    mode: cognexLightModeSchema,
    startPower: cognexLightPowerSchema,
    ledMask: z.number().int().optional().nullable(),
});
export const LightingPlanAutoAdjustSchema = z.object({
    targetGray: z.number().int().optional(),
    tolerance: z.number().int().optional(),
    maxIterations: z.number().int().optional(),
});
export const LightingPlanImageQualitySchema = z.object({
    saturationPercentileThreshold: z.number().optional(),
    darkPercentileThreshold: z.number().optional(),
    saturationPercentageLimit: z.number().optional(),
});
export const LightingPlanSchema = z.object({
    modes: z.array(LightModeEntrySchema).min(1),
    exhaustive: z.boolean(),
    autoAdjust: LightingPlanAutoAdjustSchema.optional(),
    imageQualityOptions: LightingPlanImageQualitySchema.optional(),
    /** When true, do not merge gateway S3 autoAdjust defaults (manual station acquire). */
    disableAutoAdjust: z.boolean().optional(),
});
export const lightingSourceSchema = z.enum(['template_starting', 'wafer_learned']);
/** Lossless upload format for wafer fingerprint images (S3 presign + edge PUT). */
export const WAFER_IMAGING_UPLOAD_CONTENT_TYPE = 'image/png';
export const WAFER_IMAGING_UPLOAD_FILE_EXTENSION = '.png';
/** S3 key segment under tenant/{tenantId}/ for batch inspection uploads (paired with jobId). */
export const WAFER_IMAGING_UPLOAD_KEY_SEGMENT = 'jobs';
/** Same values as schemas.waferSizeSchema (z.nativeEnum(WAFER_SIZE)); defined here to avoid circular import with schemas.ts barrel. */
export const waferImagingWaferSizeSchema = z.nativeEnum(dt.WAFER_SIZE);
export const waferImagingIdSideSchema = z.enum(['top', 'bottom']);
export const LearnedLightingModeEntrySchema = z.object({
    mode: cognexLightModeSchema,
    startPower: cognexLightPowerSchema,
    finalPower: cognexLightPowerOptionalSchema,
    ledMask: z.number().int().optional().nullable(),
    rank: z.number().int().optional(),
    ocrConfidence: z.number().optional(),
});
export const WaferImagingTemplateSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.string().uuid(),
    name: z.string().min(1),
    waferSize: waferImagingWaferSizeSchema,
    idSide: waferImagingIdSideSchema,
    markOffsetDeg: z.number(),
    startingLightingModes: z.array(LightModeEntrySchema).min(1),
    isSystemTemplate: z.boolean(),
});
export const EfemPeripheralBaseSchema = z.object({
    parentGatewaySerialNumber: z.string().min(1),
    displayLabel: z.string().optional(),
});
export const EfemPeripheralWaferIdCameraSchema = EfemPeripheralBaseSchema.extend({
    supportedWaferSize: waferImagingWaferSizeSchema,
    idSide: waferImagingIdSideSchema,
    mountCalibrationDeg: z.number(),
});
export const EfemPeripheralBarcodeScannerSchema = EfemPeripheralBaseSchema.extend({
    loadPortId: z.number().int().min(1),
});
/** Handheld scanner — gateway accessory, not tied to a load port. */
export const EfemPeripheralHandheldBarcodeScannerSchema = EfemPeripheralBaseSchema;
/** Load port size on the physical EFEM (matches hardware adapter / LoadPortSchema.size). */
export const efemLoadPortSizeSchema = z.nativeEnum(dt.EFEM_LOAD_PORT_SIZE);
export const EfemGatewayLoadPortConfigSchema = z.object({
    id: z.number().int().positive(),
    size: efemLoadPortSizeSchema,
});
/**
 * Tenant-configured EFEM hardware topology (ground truth for load ports).
 * Stored on efem_clients.hardware_profile — set by tenant admin at setup.
 */
export const EfemGatewayHardwareProfileSchema = z
    .object({
    loadPorts: z.array(EfemGatewayLoadPortConfigSchema).min(1).max(8),
})
    .superRefine((val, ctx) => {
    const ids = val.loadPorts.map((p) => p.id);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Load port ids must be unique',
            path: ['loadPorts'],
        });
    }
});
export const PutEfemGatewayHardwareProfileSchema = EfemGatewayHardwareProfileSchema;
export const EfemGatewayHardwareProfileResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        efemClientId: z.number().int(),
        gatewaySerialNumber: z.string().nullable(),
        gatewayName: z.string().nullable(),
        hardwareProfile: EfemGatewayHardwareProfileSchema.nullable(),
        configured: z.boolean(),
    }),
});
/** PATCH body: camera or scanner binding; null clears installation. */
export const PatchEfemPeripheralBodySchema = z.object({
    efemPeripheral: z
        .union([
        EfemPeripheralWaferIdCameraSchema,
        EfemPeripheralBarcodeScannerSchema,
        EfemPeripheralHandheldBarcodeScannerSchema,
    ])
        .nullable(),
});
export const WaferImagingTemplateListItemSchema = WaferImagingTemplateSchema.pick({
    id: true,
    name: true,
    waferSize: true,
    idSide: true,
    markOffsetDeg: true,
    isSystemTemplate: true,
    startingLightingModes: true,
});
export const WaferImagingTemplateListResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        templates: z.array(WaferImagingTemplateListItemSchema),
    }),
});
export const WaferImagingTemplateIdParamSchema = z.object({
    id: z.coerce.number().int().positive(),
});
function validateOperatorLightingModes(modes, ctx, pathPrefix) {
    for (let i = 0; i < modes.length; i++) {
        if (modes[i].mode > COGNEX_LIGHT_MODE_OPERATOR_MAX) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Light mode ${modes[i].mode} requires extra hardware setup and is not available in tenant templates`,
                path: [...pathPrefix, i, 'mode'],
            });
        }
    }
}
const WaferImagingTemplateBodySchema = z.object({
    name: z.string().min(1).max(255),
    waferSize: waferImagingWaferSizeSchema,
    idSide: waferImagingIdSideSchema,
    markOffsetDeg: z.number().min(-360).max(360),
    startingLightingModes: z.array(LightModeEntrySchema).min(1),
});
export const CreateWaferImagingTemplateRequestSchema = WaferImagingTemplateBodySchema.superRefine((val, ctx) => {
    validateOperatorLightingModes(val.startingLightingModes, ctx, ['startingLightingModes']);
});
export const UpdateWaferImagingTemplateRequestSchema = WaferImagingTemplateBodySchema.partial().superRefine((val, ctx) => {
    if (val.startingLightingModes) {
        validateOperatorLightingModes(val.startingLightingModes, ctx, ['startingLightingModes']);
    }
});
export const WaferImagingTemplateResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        template: WaferImagingTemplateSchema,
    }),
});
export const EfemPeripheralMatrixSlotSchema = z.object({
    roleKey: z.string(),
    roleLabel: z.string(),
    boundSerialNumber: z.string().nullable(),
    displayLabel: z.string().nullable(),
    configured: z.boolean(),
});
/** Wafer camera matrix slot — includes binding fields from device efemPeripheral when configured. */
export const EfemPeripheralWaferCameraMatrixSlotSchema = EfemPeripheralMatrixSlotSchema.extend({
    supportedWaferSize: waferImagingWaferSizeSchema.optional(),
    idSide: waferImagingIdSideSchema.optional(),
    mountCalibrationDeg: z.number().optional(),
});
export const EfemPeripheralMatrixResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        efemClientId: z.number().int(),
        gatewaySerialNumber: z.string(),
        gatewayName: z.string().nullable(),
        loadPorts: z.array(EfemGatewayLoadPortConfigSchema),
        hardwareProfileConfigured: z.boolean(),
        barcodeScanners: z.array(EfemPeripheralMatrixSlotSchema),
        handheldBarcodeScanner: EfemPeripheralMatrixSlotSchema.nullable(),
        waferCameras: z.array(EfemPeripheralWaferCameraMatrixSlotSchema),
    }),
});
export const ClearLearnedLightingResponseSchema = z.object({
    success: z.literal(true),
    message: z.string(),
});
export const AlignTargetSchema = z.object({
    alignTargetDeg: z.number(),
});
export const InspectCameraEntrySchema = z
    .object({
    serialNumber: z.string().min(1),
    alignTargetDeg: z.number().optional(),
    alignTargets: z.array(AlignTargetSchema).min(1).optional(),
    lightingPlan: LightingPlanSchema,
})
    .superRefine((val, ctx) => {
    const hasRoot = val.alignTargetDeg !== undefined;
    const hasList = val.alignTargets !== undefined && val.alignTargets.length > 0;
    if (hasRoot === hasList) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Each cameras[] entry must set alignTargetDeg OR alignTargets[], not both or neither',
        });
    }
});
export const InspectUploadInstructionSchema = z.object({
    serialNumber: z.string().min(1),
    slotNumber: z.number().int().positive(),
    alignTargetDeg: z.number(),
    modeIndex: z.number().int().min(0),
    imageIndex: z.number().int().min(0).default(0),
    objectKey: z.string().min(1),
    uploadUrl: z.string().url(),
    expiresAt: z.string(),
});
export const CaptureUploadInstructionSchema = z.object({
    modeIndex: z.number().int().min(0),
    imageIndex: z.number().int().min(0).default(0),
    objectKey: z.string().min(1),
    uploadUrl: z.string().url(),
    expiresAt: z.string(),
});
export const CaptureAtAnglesUploadInstructionSchema = CaptureUploadInstructionSchema.extend({
    angleIndex: z.number().int().min(0),
    alignTargetDeg: z.number(),
});
export const CapturedImageUploadSchema = z.object({
    status: z.enum(['success', 'failed', 'expired']),
    objectKey: z.string().optional(),
    size: z.number().int().optional(),
    error: z.string().optional(),
    timestamp: z.string().optional(),
});
export const CapturedImageSchema = z.object({
    serialNumber: z.string().optional(),
    alignTargetDeg: z.number().optional(),
    angleIndex: z.number().int().optional(),
    modeIndex: z.number().int(),
    imageIndex: z.number().int(),
    lightMode: cognexLightModeSchema.optional(),
    startPower: cognexLightPowerOptionalSchema,
    finalPower: cognexLightPowerOptionalSchema,
    usedAutoAdjust: z.boolean().optional(),
    exhaustive: z.boolean().optional(),
    edgeQuality: z
        .object({
        isGrosslyBad: z.boolean(),
        highPercentileGray: z.number().optional(),
        reasons: z.array(z.string()).optional(),
    })
        .optional(),
    /** Camera-native capture format; uploads are always PNG (see WAFER_IMAGING_UPLOAD_*). */
    format: z.enum(['bmp', 'jpeg', 'png']).optional(),
    capturedAt: z.string(),
    upload: CapturedImageUploadSchema,
});
export const InspectContainerSlotCommandSchema = z.object({
    command: z.literal('inspect_container_slot'),
    loadPortId: z.number().int().positive(),
    slotNumber: z.number().int().positive(),
    jobId: z.string().min(1),
    cameras: z.array(InspectCameraEntrySchema).min(1),
    uploadInstructions: z.array(InspectUploadInstructionSchema).min(1),
});
export const InspectContainerSlotsCommandSchema = z.object({
    command: z.literal('inspect_container_slots'),
    loadPortId: z.number().int().positive(),
    slotNumbers: z.array(z.number().int().positive()).min(1),
    jobId: z.string().min(1),
    cameras: z.array(InspectCameraEntrySchema).min(1),
    uploadInstructions: z.array(InspectUploadInstructionSchema).min(1),
    options: z.record(z.unknown()).optional(),
});
export const CaptureWaferImagesCommandSchema = z.object({
    command: z.literal('capture_wafer_images'),
    serialNumber: z.string().min(1),
    lightingPlan: LightingPlanSchema,
    uploadInstructions: z.array(CaptureUploadInstructionSchema).min(1),
    options: z
        .object({
        timeoutMs: z.number().int().positive().optional(),
        /** When true, skip prealigner occupancy check (station acquire / external align). */
        skipHardwareGuards: z.boolean().optional(),
        purpose: z.enum(['station_acquire']).optional(),
    })
        .optional(),
});
export const CaptureWaferImagesAtAnglesCommandSchema = z.object({
    command: z.literal('capture_wafer_images_at_angles'),
    serialNumber: z.string().min(1),
    alignTargets: z.array(AlignTargetSchema).min(1),
    lightingPlan: LightingPlanSchema,
    uploadInstructions: z.array(CaptureAtAnglesUploadInstructionSchema).min(1),
    options: z
        .object({
        timeoutMs: z.number().int().positive().optional(),
    })
        .optional(),
});
export const ContainerSlotInspectionProgressSchema = z.object({
    jobId: z.string(),
    serialNumber: z.string(),
    slotNumber: z.number().int(),
    status: z.string(),
    alignTargetDeg: z.number().optional(),
    modeIndex: z.number().int().optional(),
    result: z
        .object({
        capturedImages: z.array(CapturedImageSchema).optional(),
        error: z.string().optional(),
    })
        .optional(),
    timestamp: z.string(),
});
//# sourceMappingURL=efem-wafer-imaging.schemas.js.map